import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-hot-toast';
import { getApiUrl } from '../utils/urls';

const API_URL = getApiUrl();
export const GLOBAL_LOADING_EVENT = 'ef-global-loading';
let activeApiRequests = 0;

const emitGlobalLoadingState = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(GLOBAL_LOADING_EVENT, {
    detail: { loading: activeApiRequests > 0 }
  }));
};

const beginGlobalLoading = () => {
  activeApiRequests += 1;
  emitGlobalLoadingState();
};

const endGlobalLoading = () => {
  activeApiRequests = Math.max(0, activeApiRequests - 1);
  emitGlobalLoadingState();
};

// Создание экземпляра axios
const api = axios.create({
  baseURL: API_URL,
  timeout: 5 * 60 * 1000, // 5 минут для загрузки больших файлов
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерцептор запросов
api.interceptors.request.use(
  (config) => {
    const skipGlobalLoader = config?.skipGlobalLoader === true;
    config.__trackGlobalLoader = !skipGlobalLoader;
    if (!skipGlobalLoader) {
      beginGlobalLoading();
    }

    const token = localStorage.getItem('token') || Cookies.get('token');
    const language = Cookies.get('language') || 'ru';
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    config.headers['Accept-Language'] = language;
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Интерцептор ответов
api.interceptors.response.use(
  (response) => {
    if (response?.config?.__trackGlobalLoader) {
      endGlobalLoading();
    }
    return response;
  },
  (error) => {
    if (error?.config?.__trackGlobalLoader) {
      endGlobalLoading();
    }

    const { response, config } = error;
    const requestUrl = String(config?.url || '');
    const isAuthRequest = requestUrl.includes('/auth/');
    
    // Проверяем, является ли запрос связанным с уроками (lessons)
    const isLessonRelatedRequest = config && (
      config.url?.includes('/lessons/') || 
      config.url?.includes('/lessons') ||
      config.url?.includes('access') ||
      config.url?.startsWith('/api/lessons/')
    );
    
    if (response?.status === 401) {
      // Для auth-запросов (например, неверный логин/пароль) отдаём ошибку вызывающему коду без редиректа.
      if (isAuthRequest) {
        return Promise.reject(error);
      }

      const hasToken = Boolean(localStorage.getItem('token') || Cookies.get('token'));
      localStorage.removeItem('token');
      Cookies.remove('token');

      // Редиректим только если это реально истекшая/невалидная сессия.
      if (hasToken && window.location.pathname !== '/auth') {
        window.location.href = '/auth';
      }
    } else if (!isLessonRelatedRequest) {
      // Показываем уведомления только для запросов, НЕ связанных с уроками
      if (response?.status === 403) {
        toast.error('Доступ запрещён');
      } else if (response?.status === 404) {
        toast.error('Ресурс не найден');
      } else if (response?.status >= 500) {
        toast.error('Ошибка сервера. Попробуйте позже');
      } else if (error.code === 'NETWORK_ERROR') {
        toast.error('Ошибка сети. Проверьте подключение');
      }
    }
    
    return Promise.reject(error);
  }
);

// API методы
export const authAPI = {
  login: async (credentials) => {
    console.log('Попытка входа с:', credentials);
    return api.post('/auth/login', credentials);
  },

  register: async (userData) => {
    console.log('API register called with userData:', userData);
    console.log('userData type:', typeof userData);
    console.log('userData keys:', Object.keys(userData));
    console.log('name value:', userData.name);
    console.log('email value:', userData.email);
    console.log('password value:', userData.password);
    
    return api.post('/auth/register', userData);
  },

  logout: async () => {
    console.log('Выход из системы');
    return api.post('/auth/logout');
  },

  getProfile: async () => {
    const token = localStorage.getItem('token') || Cookies.get('token');
    console.log('Получение профиля с токеном:', token);
    
    if (!token) {
      throw new Error('Токен не найден');
    }

    return api.get('/auth/me');
  },

  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post(`/auth/reset-password/${token}`, { password }),
  verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
  verifyEmailCode: (email, code) => api.post('/auth/verify-email', { email, code }),
  resendVerificationCode: (email) => api.post('/auth/resend-verification', { email }),
  resetPasswordWithAdminCode: (payload) => api.post('/auth/admin-reset-password', payload),
  getAntiBotChallenge: () => api.get('/auth/antibot/challenge'),
};

export const userAPI = {
  updateProfile: (data) => api.put('/users/profile', data),
  changePassword: (data) => api.put('/users/change-password', data),
  getOrders: (params) => api.get('/users/orders', { params }),
  getCourses: (params) => api.get('/users/courses', { params }),
};

export const productsAPI = {
  getProducts: (params) => api.get('/products', { params }),
  getProduct: (id) => api.get(`/products/${id}`),
  getCategories: () => api.get('/products/categories'),
  searchProducts: (query) => api.get(`/api/products/search?q=${query}`)
};

export const coursesAPI = {
  getAllCourses: () => api.get('/courses'),
  getCourse: (id) => api.get(`/courses/${id}`),
  enrollInCourse: (id) => api.post(`/courses/${id}/enroll`)
};

export const lessonsAPI = {
  // Public endpoints
  getPublicLessons: () => api.get('/lessons/public'),
  getPublicLesson: (id) => api.get(`/lessons/public/${id}`),
  
  // Admin endpoints
  getAllLessons: () => api.get('/lessons'),
  createLesson: (data) => api.post('/lessons', data),
  updateLesson: (id, data) => api.put(`/lessons/${id}`, data),
  deleteLesson: (id) => api.delete(`/lessons/${id}`)
};

export const ordersAPI = {
  createOrder: (orderData) => api.post('/orders', orderData),
  getMyOrders: () => api.get('/orders/my'),
  getOrder: (id) => api.get(`/orders/${id}`)
};

export const portfolioAPI = {
  getPortfolio: (params) => api.get('/portfolio', { params }),
  getPortfolioItem: (id) => api.get(`/portfolio/${id}`),
  getAdminPortfolio: () => api.get('/admin/portfolio'),
  createPortfolio: (data, config = {}) => api.post('/admin/portfolio', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...config,
  }),
  updatePortfolio: (id, data, config = {}) => api.put(`/admin/portfolio/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...config,
  }),
  deletePortfolio: (id) => api.delete(`/admin/portfolio/${id}`),
};

export const articlesAPI = {
  getArticles: (params) => api.get('/articles', { params }),
  getArticle: (slug) => api.get(`/articles/${slug}`),
  getAdminArticles: () => api.get('/admin/articles'),
  createArticle: (data) => api.post('/admin/articles', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  updateArticle: (id, data) => api.put(`/admin/articles/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  deleteArticle: (id) => api.delete(`/admin/articles/${id}`)
};

export const landingAPI = {
  // About section
  getAboutContent: () => api.get('/landing/about'),
  updateAboutContent: (data) => {
    if (data instanceof FormData) {
      return api.put('/landing/about', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.put('/landing/about', data);
  },
  
  // Landing page general content
  getLandingContent: () => api.get('/landing'),
  updateLandingContent: (data) => api.put('/landing', data),

  // News
  getNews: () => api.get('/landing/news'),
  getAdminNews: () => api.get('/admin/news'),
  createNews: (data) => {
    if (data instanceof FormData) {
      return api.post('/admin/news', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.post('/admin/news', data);
  },
  updateNews: (id, data) => {
    if (data instanceof FormData) {
      return api.put(`/admin/news/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.put(`/admin/news/${id}`, data);
  },
  deleteNews: (id) => api.delete(`/admin/news/${id}`),
};

export const cartService = {
  getCart: () => api.get('/cart'),
  addToCart: (productId, quantity = 1) => api.post('/cart/add', { productId, quantity }),
  updateQuantity: (itemId, quantity) => api.put(`/cart/items/${itemId}`, { quantity }),
  removeItem: (itemId) => api.delete(`/cart/items/${itemId}`),
  clearCart: () => api.delete('/cart'),
};

// Order API
export const orderAPI = {
  // Создание заказа
  createOrder: (orderData) => api.post('/orders', orderData),
  
  // Получение заказа по ID или номеру
  getOrder: (identifier) => api.get(`/orders/${identifier}`),
  
  // Получение списка заказов (для пользователя или админа)
  getOrders: (params) => api.get('/orders', { params }),
  
  // Получение заказов пользователя
  getUserOrders: () => api.get('/orders/user'),
  
  // Обновление статуса заказа (только админ)
  updateOrderStatus: (orderId, data) => api.patch(`/orders/${orderId}/status`, data)
};

export const adminAPI = {
  // Users
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  
  // Courses
  createCourse: (data) => api.post('/admin/courses', data),
  updateCourse: (id, data) => api.put(`/admin/courses/${id}`, data),
  deleteCourse: (id) => api.delete(`/admin/courses/${id}`),
  
  // Portfolio
  createPortfolioItem: (data) => api.post('/admin/portfolio', data),
  updatePortfolioItem: (id, data) => api.put(`/admin/portfolio/${id}`, data),
  deletePortfolioItem: (id) => api.delete(`/admin/portfolio/${id}`),
  
  // Analytics
  getDashboard: () => api.get('/admin/dashboard'),
  getAnalytics: (params) => api.get('/admin/analytics', { params }),
};

export const uploadAPI = {
  uploadFile: (file, type = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    
    return api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  uploadMultiple: (files, type = 'general') => {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`files`, file);
    });
    formData.append('type', type);
    
    return api.post('/upload/multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const shopAPI = {
  // Получить все товары или по категории
  getProducts: (params = {}) => api.get('/shop', { params }),
  
  // Получить товар по ID
  getProduct: (id) => api.get(`/shop/${id}`),
  
  // Создать новый товар
  createProduct: (productData) => {
    return api.post('/shop', productData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Обновить товар
  updateProduct: (id, productData) => {
    return api.put(`/shop/${id}`, productData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Удалить товар
  deleteProduct: (id) => api.delete(`/shop/${id}`),
  
  // Удалить изображение товара
  deleteProductImage: (id, imageIndex) => api.delete(`/shop/${id}/image/${imageIndex}`),
};

// API для работы с отзывами
export const reviewsAPI = {
  // Получить все одобренные отзывы
  getReviews: () => api.get('/reviews'),
  getReview: (id) => api.get(`/reviews/${id}`),
  
  // Создать новый отзыв
  createReview: (formData) => api.post('/reviews', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // Получить все отзывы для админпанели
  getAdminReviews: (params) => api.get('/reviews/admin', { params }),
  
  // Одобрить отзыв
  approveReview: (id) => api.put(`/reviews/admin/${id}/approve`),
  
  // Отклонить отзыв
  rejectReview: (id) => api.put(`/reviews/admin/${id}/reject`),
  
  // Удалить отзыв
  deleteReview: (id) => api.delete(`/reviews/admin/${id}`),
  
  // Изменить порядок отзывов
  reorderReviews: (reviews) => api.put('/reviews/admin/reorder', { reviews }),
};

// Sales API
export const salesAPI = {
  // Получить покупки уроков пользователя
  getUserSales: () => api.get('/sales/user'),
};

export default api;
